import Link from "next/link";
import React from "react";

type ButtonProps = {
  label: string;
  range: number;
  href?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isActive?: boolean;
  color: "yellow" | "violet" | "fuchsia";
  ariaLabel?: string;
  type?: "button" | "submit" | "reset";
  className?: string;
};

const getButtonClasses = (
  isActive: boolean | undefined,
  range: number,
  color: "yellow" | "violet" | "fuchsia",
  className?: string,
) => {
  return [
    "w-fit rounded-full px-4 py-2 font-semibold",
    isActive && range === 1
      ? `cursor-pointer ${
          color === "yellow"
            ? "bg-yellow-900 text-yellow-50 hover:ring-yellow-950 active:bg-yellow-950"
            : color === "violet"
              ? "bg-violet-900 text-white hover:ring-violet-950 active:bg-violet-950"
              : "bg-fuchsia-500 hover:ring-fuchsia-600 active:bg-fuchsia-600"
        } ring-inset hover:ring hover:transition-all`
      : !isActive && range === 1
        ? `cursor-not-allowed ${
            color === "yellow"
              ? "bg-yellow-500/40 text-yellow-600/50"
              : color === "violet"
                ? "bg-violet-500/40 text-violet-600/50"
                : "bg-fuchsia-500/40 text-fuchsia-600/50"
          } hover:transition-all`
        : isActive && range === 2
          ? `cursor-pointer border ${
              color === "yellow"
                ? "border-yellow-600 text-yellow-950 hover:ring-yellow-500 active:bg-yellow-500"
                : color === "violet"
                  ? "border-violet-600 text-violet-950 hover:ring-violet-400 active:bg-violet-500"
                  : "border-fuchsia-600 text-fuchsia-950 hover:ring-fuchsia-500 active:bg-fuchsia-500"
            } ring-inset hover:ring hover:transition-all`
          : !isActive && range === 2
            ? "cursor-not-allowed"
            : "",
    className || "",
  ].join(" ");
};

export const Button: React.FC<ButtonProps> = ({
  label,
  range,
  href,
  onClick,
  isActive,
  color,
  ariaLabel,
  type = "button",
  className,
}) => {
  const classes = getButtonClasses(isActive, range, color, className);
  if (href) {
    return (
      <Link href={href} aria-label={ariaLabel || label} className={classes}>
        {label}
      </Link>
    );
  }
  return (
    <button
      type={type}
      aria-label={ariaLabel || label}
      className={classes}
      onClick={onClick}
    >
      {label}
    </button>
  );
};

export default Button;
